/**
 * /smartstart — borrower-facing Doorly SmartStart™ portal.
 *
 * Three states:
 *   - Not enrolled  → returns to /dashboard with a friendly message.
 *   - Enrolled, locked → shows the 6-card grid in muted/lock state, no
 *                        interaction. Copy explains it unlocks at home_shopping.
 *   - Enrolled, unlocked → grid of 6 cards (locked/available/in-progress/
 *                          complete states). Click an available card →
 *                          inline expand to ModuleReader.
 *
 * The ModuleReader renders the lesson content + a 3-question quiz at
 * the bottom. Submit → score is computed locally, POSTed to backend with
 * the answer indexes; backend records progress, fires completion email
 * + certificate when all 6 are done.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { SMARTSTART_MODULES, TOTAL_MODULES, QUIZ_PASS_THRESHOLD } from '../constants/smartStartModules';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PRIMARY = '#3a6ad9';
const NEAR_BLACK = '#111111';
const LIME = '#acf44e';
const YELLOW = '#ffdd86';

const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

export default function SmartStart() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [error, setError] = useState('');
  // Confetti + "View Certificate" modal shown when the final module flips
  // the borrower to fully complete. Triggered by handleModuleComplete on
  // the {completed: true} response.
  const [showCelebration, setShowCelebration] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Authenticated certificate viewer — the /certificate endpoint enforces
  // requireAuth, so opening it via <a target="_blank"> drops the Bearer
  // header and the borrower lands on a JSON "Authentication required"
  // page. Fetch as a blob with the auth header, then open a blob: URL.
  //
  // With responseType: 'blob', error bodies arrive as Blobs too, so the
  // catch reads the blob as text to surface the real server message
  // instead of falling through to a generic 'try again' alert.
  const handleViewCertificate = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/smartstart/certificate`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = err?.message || 'Unable to open certificate. Please try again.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = (() => { try { return JSON.parse(text); } catch (_) { return null; } })();
          msg = parsed?.error || text || msg;
        } catch (_) { /* fall through */ }
      } else if (data?.error) {
        msg = data.error;
      }
      console.error('[SmartStart] View Certificate failed:', err?.response?.status, msg);
      alert(msg);
    }
  }, [headers]);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/smartstart/status`, { headers })
      .then((r) => setStatus(r.data || { enrolled: false }))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load SmartStart.'))
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (loading) {
    return <Shell><div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading SmartStart…</div></Shell>;
  }
  if (error) {
    return <Shell><div style={{ padding: 40, textAlign: 'center', color: '#ff082f' }}>{error}</div></Shell>;
  }
  if (!status?.enrolled) {
    return (
      <Shell>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: NEAR_BLACK }}>You're not enrolled in SmartStart™</h2>
          <p style={{ margin: '10px 0 18px', color: '#6b7280', fontSize: 14, lineHeight: 1.7 }}>
            Doorly SmartStart™ is our Homebuyer Success Program for borrowers with a qualifying FICO under 620 or first-time homebuyers. If you believe you should be enrolled, contact <a href="mailto:support@godoorly.com" style={{ color: PRIMARY }}>support@godoorly.com</a>.
          </p>
          <button onClick={() => navigate('/dashboard')} type="button"
            style={{ padding: '10px 20px', background: PRIMARY, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            Back to Dashboard
          </button>
        </div>
      </Shell>
    );
  }

  const locked = !status.unlockedAt;
  const completed = !!status.completedAt;
  const completedCount = status.completedCount || 0;
  const pct = Math.round((completedCount / TOTAL_MODULES) * 100);

  const handleModuleComplete = async (moduleNumber, quizAnswers, score) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/smartstart/modules/${moduleNumber}/complete`,
        { quizAnswers, score }, { headers }
      );
      if (r.data?.completed) {
        // All 6 modules finished — fire the celebration modal. The
        // backend awaits issueCertificate inside this same response so
        // the certificate PDF is already on disk by the time we get here.
        setShowCelebration(true);
      }
      // Refetch status so the grid reflects the new state.
      load();
      setActiveModule(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record module completion.');
    }
  };

  const handleModuleStart = async (moduleNumber) => {
    try {
      await axios.post(`${API_URL}/api/smartstart/modules/${moduleNumber}/start`, {}, { headers });
    } catch { /* non-fatal */ }
  };

  return (
    <Shell>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: NEAR_BLACK }}>
          Doorly SmartStart<sup style={{ fontSize: '0.55em' }}>™</sup>
        </h1>
        <p style={{ margin: '4px 0 16px', color: '#6b7280', fontSize: 14 }}>
          Homebuyer Success Program — {TOTAL_MODULES} modules, ~{TOTAL_MODULES * 8} minutes total
        </p>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: NEAR_BLACK, textTransform: 'uppercase', letterSpacing: 0.06 }}>
              Your Progress
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: completed ? '#166534' : NEAR_BLACK }}>
              {completedCount} of {TOTAL_MODULES} modules complete
            </div>
          </div>
          <div style={{ height: 10, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: completed ? LIME : PRIMARY, transition: 'width 0.3s ease' }} />
          </div>
          {completed && (
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: '#166534' }}>
              ✅ Completed {fmtDate(status.completedAt)}
              {status.certificateDocumentId && (
                <button type="button" onClick={handleViewCertificate}
                  style={{ marginLeft: 12, background: 'none', border: 'none', padding: 0, color: PRIMARY, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  View Certificate →
                </button>
              )}
            </div>
          )}
          {!completed && locked && (
            <div style={{ marginTop: 10, padding: '10px 12px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: 8, fontSize: 13, color: '#7a5200', lineHeight: 1.6 }}>
              SmartStart unlocks once you accept your SmartEstimate and advance to home shopping.
            </div>
          )}
        </div>
      </div>

      {/* Module grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 14 }}>
        {SMARTSTART_MODULES.map((m) => {
          const p = (status.progress || []).find((row) => row.module_number === m.number) || {};
          const isComplete = !!p.completed_at;
          const isInProgress = !!p.started_at && !p.completed_at;
          // Sequential unlock: module N is gated on module N-1 being complete.
          const prevComplete = m.number === 1
            ? true
            : !!(status.progress || []).find((row) => row.module_number === m.number - 1)?.completed_at;
          const isLocked = locked || !prevComplete;
          const isActive = activeModule === m.number;
          return (
            <ModuleCard key={m.number}
              module={m}
              progress={p}
              locked={isLocked}
              complete={isComplete}
              inProgress={isInProgress}
              active={isActive}
              onOpen={() => {
                if (isLocked) return;
                setActiveModule(m.number);
                handleModuleStart(m.number);
                window.scrollTo({ top: window.innerHeight * 0.5, behavior: 'smooth' });
              }}
            />
          );
        })}
      </div>

      {/* Inline module reader */}
      {activeModule != null && (() => {
        const m = SMARTSTART_MODULES.find((x) => x.number === activeModule);
        if (!m) return null;
        return (
          <ModuleReader
            module={m}
            onClose={() => setActiveModule(null)}
            onComplete={(answers, score) => handleModuleComplete(m.number, answers, score)}
          />
        );
      })()}

      {showCelebration && (
        <CompletionCelebration
          onClose={() => setShowCelebration(false)}
          onViewCertificate={handleViewCertificate}
          hasCertificate={!!status?.certificateDocumentId}
        />
      )}
    </Shell>
  );
}

// Full-screen confetti + "View Certificate" celebration shown when the
// borrower finishes the final SmartStart module. Inline 80-piece confetti
// burst (same look as Dashboard's confettiPieces pattern) plus a centered
// success card with two CTAs.
function CompletionCelebration({ onClose, onViewCertificate, hasCertificate }) {
  const confettiPieces = useMemo(
    () => Array.from({ length: 80 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2.5,
      dur: 2.4 + Math.random() * 3.2,
      color: ['#acf44e', '#3a6ad9', '#fffd5b', '#ff082f', '#ffdd86', '#fff'][Math.floor(Math.random() * 6)],
      size: 6 + Math.random() * 10,
      rotate: Math.random() * 360,
    })),
    []
  );
  return (
    <div className="safe-modal-overlay" style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: 16, overflow: 'hidden',
    }}>
      {/* Confetti layer — sits above the overlay scrim so pieces pass over the modal too */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {confettiPieces.map((p) => (
          <span key={p.id}
            style={{
              position: 'absolute', top: '-10%', left: `${p.left}%`,
              width: p.size, height: p.size, background: p.color, borderRadius: 2,
              animation: `ss-confetti ${p.dur}s ${p.delay}s linear infinite`,
              transform: `rotate(${p.rotate}deg)`,
            }} />
        ))}
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        background: '#fff', borderRadius: 18, padding: 'clamp(28px, 4vw, 40px)',
        width: '100%', maxWidth: 480, textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', background: '#f0fde4',
          color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 44, margin: '0 auto 16px', border: '2px solid #acf44e',
        }}>🎓</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 800, color: NEAR_BLACK }}>
          Congratulations!
        </h2>
        <p style={{ margin: '0 0 22px', color: '#374151', fontSize: 15, lineHeight: 1.6 }}>
          You've completed all 6 modules of Doorly SmartStart™. Your Certificate of Completion is ready —
          this fulfills the SmartStart closing requirement.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hasCertificate && (
            <button type="button" onClick={onViewCertificate}
              style={{ padding: '13px 22px', background: PRIMARY, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 800 }}>
              View Certificate
            </button>
          )}
          <button type="button" onClick={onClose}
            style={{ padding: '11px 22px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            Close
          </button>
        </div>
      </div>
      <style>{`
        @keyframes ss-confetti {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', maxWidth: '100%', overflowX: 'hidden' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 16px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/dashboard')} type="button"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: PRIMARY, fontSize: 13, fontWeight: 700 }}>
            ← Back to Dashboard
          </button>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            SmartStart™
          </span>
        </div>
      </div>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px 60px', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}

function ModuleCard({ module: m, progress, locked, complete, inProgress, active, onOpen }) {
  const borderColor = complete ? LIME : inProgress ? YELLOW : locked ? '#e5e7eb' : PRIMARY;
  const bg = locked ? '#f9fafb' : '#fff';
  const opacity = locked ? 0.7 : 1;
  return (
    <button onClick={onOpen} disabled={locked} type="button"
      style={{
        textAlign: 'left', cursor: locked ? 'not-allowed' : 'pointer',
        background: bg, border: `2px solid ${borderColor}`,
        borderRadius: 12, padding: 18, opacity,
        outline: active ? `3px solid ${PRIMARY}` : 'none', outlineOffset: 2,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Module {m.number} of {6}
        </span>
        {complete && <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#f0fde4', padding: '2px 8px', borderRadius: 999 }}>✓ Complete</span>}
        {inProgress && !complete && <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fffbeb', padding: '2px 8px', borderRadius: 999 }}>In Progress</span>}
        {locked && <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>🔒 Locked</span>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: NEAR_BLACK, marginBottom: 6 }}>{m.title}</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>~{m.estimatedMinutes} minutes · {m.quiz.length} quiz questions</div>
      {complete && progress?.quiz_score != null && (
        <div style={{ fontSize: 11, color: '#166534', marginTop: 6, fontWeight: 600 }}>
          Quiz score: {progress.quiz_score} / {m.quiz.length}
        </div>
      )}
    </button>
  );
}

function ModuleReader({ module: m, onClose, onComplete }) {
  const [answers, setAnswers] = useState(() => Array(m.quiz.length).fill(null));
  const [submitted, setSubmitted] = useState(false);
  const [retryRound, setRetryRound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allAnswered = answers.every((a) => a != null);
  const score = answers.reduce((acc, a, i) => acc + (a === m.quiz[i].correctIndex ? 1 : 0), 0);
  const passed = score >= QUIZ_PASS_THRESHOLD;

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setSubmitted(true);
    if (passed || retryRound) {
      setSubmitting(true);
      try {
        await onComplete(answers, score);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleRetry = () => {
    setSubmitted(false);
    setRetryRound(true);
    setAnswers(Array(m.quiz.length).fill(null));
  };

  return (
    <div style={{ marginTop: 28, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 'clamp(20px, 4vw, 32px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Module {m.number} of {6}
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 800, color: NEAR_BLACK }}>{m.title}</h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Estimated time: ~{m.estimatedMinutes} minutes</div>
        </div>
        <button onClick={onClose} type="button"
          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
          Close
        </button>
      </div>

      {/* Lesson content */}
      <div style={{ maxWidth: 680, margin: '0 auto', fontSize: 16, lineHeight: 1.8, color: '#1f2937' }}>
        {m.content.map((sec, i) => (
          <div key={i} style={{ marginBottom: 22 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: NEAR_BLACK }}>{sec.heading}</h3>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8 }}>{sec.body}</p>
          </div>
        ))}
      </div>

      {/* Quiz */}
      <div style={{ marginTop: 32, paddingTop: 22, borderTop: '2px solid #f3f4f6' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: NEAR_BLACK }}>Quick Knowledge Check</h3>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
          Answer all {m.quiz.length} questions to complete this module.
        </p>
        {m.quiz.map((q, qi) => {
          const userAnswer = answers[qi];
          const correctIndex = q.correctIndex;
          const isWrong = submitted && userAnswer !== correctIndex;
          return (
            <div key={qi} style={{ marginBottom: 22, padding: '14px 16px', background: '#f9fafb', border: `1px solid ${isWrong ? '#ffd6db' : '#f3f4f6'}`, borderRadius: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: NEAR_BLACK, marginBottom: 10 }}>
                {qi + 1}. {q.question}
              </div>
              {q.options.map((opt, oi) => {
                const selected = userAnswer === oi;
                const isCorrect = oi === correctIndex;
                let bg = '#fff';
                let borderColor = '#e5e7eb';
                if (submitted) {
                  if (isCorrect) { bg = '#f0fde4'; borderColor = '#86d932'; }
                  else if (selected && !isCorrect) { bg = '#fff5f6'; borderColor = '#ff082f'; }
                } else if (selected) {
                  bg = '#eff3fe'; borderColor = PRIMARY;
                }
                return (
                  <label key={oi}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', marginBottom: 6,
                      background: bg, border: `1.5px solid ${borderColor}`,
                      borderRadius: 8, cursor: submitted ? 'default' : 'pointer',
                    }}>
                    <input type="radio" name={`q-${m.number}-${qi}`} value={oi}
                      checked={selected} disabled={submitted}
                      onChange={() => setAnswers((prev) => prev.map((a, i) => i === qi ? oi : a))}
                      style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: '#1f2937', flex: 1 }}>{opt}</span>
                    {submitted && isCorrect && <span style={{ fontSize: 11, fontWeight: 700, color: '#166534' }}>Correct</span>}
                    {submitted && selected && !isCorrect && <span style={{ fontSize: 11, fontWeight: 700, color: '#ff082f' }}>Your answer</span>}
                  </label>
                );
              })}
            </div>
          );
        })}

        {submitted ? (
          passed || retryRound ? (
            <div style={{ background: '#f0fde4', border: '1px solid #d4f9a0', color: '#166534', borderRadius: 10, padding: '14px 18px', fontSize: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                {passed ? `Nice work — ${score}/${m.quiz.length} correct.` : `Module saved — keep going!`}
              </div>
              <div>{submitting ? 'Saving your progress…' : 'Module complete. Returning to your overview.'}</div>
            </div>
          ) : (
            <div style={{ background: '#fffbef', border: '1px solid #ffe4a0', color: '#7a5200', borderRadius: 10, padding: '14px 18px', fontSize: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>
                You got {score} out of {m.quiz.length}. Take one more look at the lesson, then try again.
              </div>
              <div style={{ marginBottom: 10 }}>The correct answers are highlighted above.</div>
              <button onClick={handleRetry} type="button"
                style={{ padding: '10px 18px', background: PRIMARY, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Retry Quiz
              </button>
            </div>
          )
        ) : (
          <button onClick={handleSubmit} disabled={!allAnswered || submitting} type="button"
            style={{ width: '100%', padding: '14px', background: allAnswered ? PRIMARY : '#e5e7eb', color: allAnswered ? '#fff' : '#9ca3af', border: 'none', borderRadius: 10, cursor: allAnswered ? 'pointer' : 'not-allowed', fontSize: 15, fontWeight: 800 }}>
            {submitting ? 'Submitting…' : 'Submit Quiz'}
          </button>
        )}
      </div>
    </div>
  );
}
